import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Avatar,
  Container,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Power,
  Settings,
  Visibility,
  Edit,
  AdminPanelSettings,
  Logout,
  Refresh
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useHealth } from '../../contexts/HealthContext';
import HealthStatusChip from '../HealthStatusChip';

// Logo's uit de public/images map - dezelfde als in Login.jsx
const dyconLogo = '/images/logo_dycon.svg';
const slimverbruikLogo = '/images/logo_slimverbruik.nl.svg';

// Styled components voor logo's - dezelfde styling als in Login.jsx
const LogoContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  '& img': {
    width: '180px',
    height: 'auto',
    marginBottom: theme.spacing(1),
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
  }
}));

const BrandTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(1)
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 400,
  letterSpacing: '0.5px'
}));

// Component voor environment selectie button
const EnvironmentSelectButton = ({ environment, onSelect, getHealthDisplay }) => {
  const healthDisplay = getHealthDisplay(environment.id);
  const isUnavailable = ['unhealthy', 'unreachable', 'error'].includes(healthDisplay.status);
  const isAdmin = ['admin', 'owner'].includes(environment.permission);
  
  // Admins kunnen altijd selecteren, reguliere users alleen bij healthy environments
  const canSelect = isAdmin || !isUnavailable;
  
  return (
    <Button
      fullWidth
      variant="contained"
      onClick={() => onSelect(environment)}
      disabled={!canSelect}
      color={isUnavailable && !isAdmin ? 'inherit' : 'primary'}
    >
      {isUnavailable && !isAdmin ? 'Niet beschikbaar' : 
       isUnavailable && isAdmin ? 'Selecteren (problemen)' : 'Selecteren'}
    </Button>
  );
};

const EnvironmentSelector = ({ onManageEnvironments }) => {
  const { 
    user, 
    environments, 
    selectEnvironment, 
    logout, 
    loadEnvironments 
  } = useAuth();
  const [loading, setLoading] = useState(false);
      const { 
      allEnvironmentsHealth, 
      getHealthDisplay, 
      isChecking, 
      setEnvironments,
      startAllEnvironmentsMonitoring,
      stopAllEnvironmentsMonitoring
    } = useHealth();

    // Sync environments met HealthContext
    useEffect(() => {
      if (environments.length > 0) {
        setEnvironments(environments);
      }
    }, [environments, setEnvironments]);

    // Start all environments monitoring wanneer component mount, stop bij unmount
    useEffect(() => {
      startAllEnvironmentsMonitoring();
      return () => {
        stopAllEnvironmentsMonitoring();
      };
    }, [startAllEnvironmentsMonitoring, stopAllEnvironmentsMonitoring]);

    const handleSelectEnvironment = async (environment) => {
    setLoading(true);
    await selectEnvironment(environment);
    setLoading(false);
  };

  const handleRefresh = async () => {
    setLoading(true);
    await loadEnvironments();
    setLoading(false);
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'readonly':
        return <Visibility fontSize="small" />;
      case 'operator':
        return <Settings fontSize="small" />;
      case 'admin':
        return <Edit fontSize="small" />;
      case 'owner':
        return <AdminPanelSettings fontSize="small" />;
      default:
        return <Visibility fontSize="small" />;
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'readonly':
        return 'default';
      case 'operator':
        return 'primary';
      case 'admin':
        return 'secondary';
      case 'owner':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPermissionLabel = (permission) => {
    switch (permission) {
      case 'readonly':
        return 'Alleen lezen';
      case 'operator':
        return 'Operator';
      case 'admin':
        return 'Beheerder';
      case 'owner':
        return 'Eigenaar';
      default:
        return permission;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'maintenance':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Actief';
      case 'inactive':
        return 'Inactief';
      case 'maintenance':
        return 'Onderhoud';
      case 'error':
        return 'Fout';
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Logo Header - toegevoegd zoals in Login.jsx */}
        <LogoContainer>
          <img
            src={dyconLogo}
            alt="Dycon Logo"
          />
          <BrandTitle variant="h4" component="h1">
            AxionIQ - Slimme energie-arbitrage
          </BrandTitle>
          <SubTitle variant="body1">
          
          voor waterstof, batterijen en industriële processen
          </SubTitle>
        </LogoContainer>

        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {user?.name?.charAt(0) || user?.email?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5">
                  Welkom, {user?.name || user?.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecteer een electrolyzer omgeving om te beheren
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <Refresh />
              </IconButton>
              <Button
                variant="outlined"
                startIcon={<Logout />}
                onClick={logout}
              >
                Uitloggen
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Environments */}
        {!environments || environments.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Geen omgevingen beschikbaar
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              U heeft nog geen toegang tot electrolyzer omgevingen. 
              U kunt uw eerste omgeving aanmaken om te beginnen.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Settings />}
              onClick={onManageEnvironments || (() => {})}
              sx={{ mt: 1 }}
            >
              Eerste Omgeving Aanmaken
            </Button>
          </Alert>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Beschikbare omgevingen ({environments?.length || 0})
            </Typography>
            
            <Grid container spacing={3}>
              {(environments || []).map((environment) => (
                <Grid item xs={12} sm={6} md={4} key={environment.id}>
                  <Card 
                    elevation={3}
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, elevation 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        elevation: 6
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Power color="primary" />
                        <Typography variant="h6" component="div">
                          {environment.name}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {environment.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                          icon={getPermissionIcon(environment.permission)}
                          label={getPermissionLabel(environment.permission)}
                          color={getPermissionColor(environment.permission)}
                          size="small"
                        />
                        <HealthStatusChip
                          environmentId={environment.id}
                          size="small"
                          showLabel={true}
                          clickable={false}
                        />
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="caption" color="text.secondary">
                        Locatie: {environment.location || 'Niet opgegeven'}
                      </Typography>
                    </CardContent>
                    
                    <CardActions>
                      <EnvironmentSelectButton 
                        environment={environment}
                        onSelect={handleSelectEnvironment}
                        getHealthDisplay={getHealthDisplay}
                      />
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Footer met Slimverbruik logo - toegevoegd zoals in Login.jsx */}
        <Box sx={{ mt: 4, textAlign: 'center', pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Powered by
          </Typography>
          <img
            src={slimverbruikLogo}
            alt="Slimverbruik.nl"
            style={{ height: '24px', width: 'auto' }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default EnvironmentSelector; 